import { create } from 'zustand';
import { toast } from 'sonner';
import { servicesContainer } from '@/core/di/services-container';
import type {
  CommunityAccount,
  CommunityAccountsFilters,
} from '@/features/communities/types/community-accounts-types';

interface CommunityAccountsStore {
  // State
  accounts: CommunityAccount[];
  totalAccounts: number; // Total accounts available for the operator
  linkedAccounts: number; // Accounts linked to the current community
  isLoading: boolean;
  error: string | null;
  filters: CommunityAccountsFilters;
  currentCommunityId: number | null;

  // Actions
  loadAccounts: (communityId: number) => Promise<void>;
  upsertAccountRelation: (
    communityId: number,
    accountId: number,
    startDate: string,
    expirationDate: string | null
  ) => Promise<void>;
  setFilters: (filters: Partial<CommunityAccountsFilters>) => void;
  reset: () => void;
}

const initialState = {
  accounts: [],
  totalAccounts: 0,
  linkedAccounts: 0,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    statusFilter: 'All' as const,
    linkedFilter: 'Linked' as const, // Default to showing only linked accounts
  },
  currentCommunityId: null,
};

export const useCommunityAccountsStore = create<CommunityAccountsStore>((set) => ({
  ...initialState,

  loadAccounts: async (communityId: number) => {
    set({ isLoading: true, error: null, currentCommunityId: communityId });

    try {
      // Lazy service access inside action
      const services = servicesContainer.getServices();
      const response = await services.getCommunityAccountsUseCase.execute(communityId);

      set({
        accounts: response.accounts,
        totalAccounts: response.totalAccounts,
        linkedAccounts: response.linkedAccounts,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load community accounts';
      set({
        error: errorMessage,
        isLoading: false,
        accounts: [],
        totalAccounts: 0,
        linkedAccounts: 0,
      });
    }
  },

  upsertAccountRelation: async (
    communityId: number,
    accountId: number,
    startDate: string,
    expirationDate: string | null
  ) => {
    try {
      set({ isLoading: true, error: null });

      // Call API
      const services = servicesContainer.getServices();
      const response = await services.upsertAccountRelationUseCase.execute(
        communityId,
        accountId,
        startDate,
        expirationDate
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update account assignment');
      }

      // Update the account in the local state
      // Check if assignment is expired (expiration date is today or in the past)
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const isExpired = expirationDate ? expirationDate <= todayStr : false;

      set((state) => ({
        accounts: state.accounts.map((account) =>
          account.accountId === accountId
            ? {
                ...account,
                isAssignedToCommunity: !isExpired,
                associatedDate: isExpired ? undefined : (account.associatedDate || new Date().toISOString()),
                startDate,
                expirationDate,
              }
            : account
        ),
        isLoading: false,
      }));

      // Show success toast
      toast.success('Account assignment updated successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update account assignment';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  setFilters: (newFilters: Partial<CommunityAccountsFilters>) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
      },
    }));
  },

  reset: () => set(initialState),
}));

// Helper function for filtering accounts (pure function)
export const filterAccounts = (
  accounts: CommunityAccount[],
  filters: CommunityAccountsFilters
): CommunityAccount[] => {
  // Safety check: return empty array if accounts is not an array
  if (!Array.isArray(accounts)) {
    return [];
  }

  return accounts.filter((account) => {
    // Safety check: skip invalid account entries
    if (!account) {
      return false;
    }

    // Search filter (by first name, last name, or ID)
    const matchesSearch =
      !filters.search ||
      account.firstName.toLowerCase().includes(filters.search.toLowerCase()) ||
      account.lastName.toLowerCase().includes(filters.search.toLowerCase()) ||
      account.accountId.toString().includes(filters.search);

    // Status filter
    const matchesStatus =
      !filters.statusFilter ||
      filters.statusFilter === 'All' ||
      account.status === filters.statusFilter;

    // Linked filter
    const matchesLinked =
      !filters.linkedFilter ||
      filters.linkedFilter === 'All' ||
      (filters.linkedFilter === 'Linked' && account.isAssignedToCommunity) ||
      (filters.linkedFilter === 'NotLinked' && !account.isAssignedToCommunity);

    return matchesSearch && matchesStatus && matchesLinked;
  });
};
